import { EFFECTS } from "./effect-registry.js";
import { parseMultiActionRoute } from "./compile/route-multi.js";
import { MOMENT } from "./compile/route-effect.js";
function parseRule(rule) {
    const multi = parseMultiActionRoute(rule);
    if (multi) {
        return {
            branch: {
                family: "route",
                ruleId: rule.id,
                severity: rule.severity,
                condition: rule.condition,
                actions: multi.actions,
            },
            moment: multi.meta.moment,
            scope: MOMENT[multi.meta.moment]?.scope ?? "form",
        };
    }
    for (const effect of EFFECTS) {
        if (effect.bucket !== "check" || !effect.parse)
            continue;
        const step = effect.parse(rule);
        if (!step)
            continue;
        return {
            branch: {
                family: "check",
                ruleId: rule.id,
                effectKey: effect.key,
                severity: rule.severity,
                condition: step.condition,
                config: step.config,
                message: step.message,
            },
            moment: step.moment,
            scope: step.scope,
            fieldName: step.fieldName,
        };
    }
    return null;
}
function groupIdOf(rule) {
    const v = rule.config?.automationGroupId;
    return typeof v === "string" && v ? v : null;
}
function toAutomation(groupId, rules) {
    const parsed = [];
    for (const rule of rules) {
        const p = parseRule(rule);
        if (!p)
            return null; // one bad member orphans the whole group
        parsed.push(p);
    }
    const head = rules[0];
    const headParsed = parsed[0];
    const description = head.config?.automationDescription;
    // Automation-level flag; compileAutomation stamps it onto every member, so
    // the head is authoritative (mirrors automationDescription recovery).
    const stopAfterFirstMatch = head.config?.stopAfterFirstMatch === true;
    // stages[] stamps only onto check-family branches (compileAutomation), so a
    // mixed-family group whose head member is a route rule must not rely on
    // head.stages — recover from the first member that actually carries them.
    const stages = rules.find((r) => r.stages?.length)?.stages;
    return {
        groupId,
        name: head.name,
        description: typeof description === "string" ? description : undefined,
        isActive: rules.every((r) => r.isActive),
        scope: headParsed.scope,
        fieldName: headParsed.fieldName,
        moment: headParsed.moment,
        stages,
        overrideBehavior: head.overrideBehavior,
        overrideConfig: head.overrideConfig,
        ...(head.appliesToRoles?.length ? { appliesToRoles: head.appliesToRoles } : {}),
        ...(stopAfterFirstMatch ? { stopAfterFirstMatch: true } : {}),
        branches: parsed.map((p) => p.branch),
    };
}
/** Re-groups persisted rule attachments back into editable `Automation`s. One
 *  unparseable member orphans the WHOLE group — never half-edit a group.
 *  Orphans are returned untouched, never silently rewritten. */
export function assembleAutomations(rules) {
    const groups = new Map();
    const singles = [];
    for (const rule of rules) {
        const gid = groupIdOf(rule);
        if (gid) {
            const bucket = groups.get(gid);
            if (bucket)
                bucket.push(rule);
            else
                groups.set(gid, [rule]);
        }
        else {
            singles.push(rule);
        }
    }
    const automations = [];
    const orphans = [];
    for (const [gid, members] of groups) {
        const sorted = [...members].sort((a, b) => a.displayOrder - b.displayOrder);
        const a = toAutomation(gid, sorted);
        if (a)
            automations.push(a);
        else
            orphans.push(...sorted);
    }
    for (const rule of singles) {
        const a = toAutomation(null, [rule]);
        if (a)
            automations.push(a);
        else
            orphans.push(rule);
    }
    return { automations, orphans };
}
//# sourceMappingURL=assemble-automations.js.map